import type { LegacyDashboardClient } from '../models/LegacyDashboardClient';

type SortDirection = 'asc' | 'desc';

function getNestedValue(source: Record<string, unknown>, field: string): unknown {
  if (!field.includes('.')) return source[field];

  return field.split('.').reduce<unknown>((current, key) => {
    if (current == null || typeof current !== 'object') return null;
    return (current as Record<string, unknown>)[key];
  }, source);
}

function getSortValue(client: LegacyDashboardClient, field: string): string | number | boolean | null {
  const value = getNestedValue(client as Record<string, unknown>, field);

  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.toLocaleLowerCase();

  if (value != null && value !== '') {
    const numeric = Number(value);
    if (!Number.isNaN(numeric)) return numeric;
  }

  return null;
}

function compareValues(
  a: string | number | boolean | null,
  b: string | number | boolean | null,
  direction: SortDirection,
): number {
  if (a === b) return 0;

  const aMissing = a === null;
  const bMissing = b === null;
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;

  let result = 0;
  if (typeof a === 'boolean' && typeof b === 'boolean') {
    result = Number(a) - Number(b);
  } else if (typeof a === 'number' && typeof b === 'number') {
    result = a - b;
  } else {
    result = String(a).localeCompare(String(b), undefined, { sensitivity: 'base', numeric: true });
  }

  return direction === 'asc' ? result : -result;
}

export function sortLegacyDashboardClients(
  clients: LegacyDashboardClient[],
  sorting: { field: string; direction: SortDirection } | null,
): LegacyDashboardClient[] {
  if (!sorting?.field) return clients;

  return [...clients].sort((left, right) => {
    const leftValue = getSortValue(left, sorting.field);
    const rightValue = getSortValue(right, sorting.field);
    const primary = compareValues(leftValue, rightValue, sorting.direction);
    if (primary !== 0) return primary;

    return compareValues(getSortValue(left, 'legacy_team_id'), getSortValue(right, 'legacy_team_id'), 'asc');
  });
}
